/**
 * <product-food-info> updates the ingredients/allergens/nutrition/net-weight
 * block when the customer changes variant. It listens at the document level
 * for 'change' bubbling from the hidden variant id input inside this
 * product's form (the same input every native and custom variant picker in
 * this theme ultimately updates), rather than assuming any particular
 * variant-picker implementation - this works for both the standard button
 * variant picker (Storpåse godis, Karamellpåse 200g) and the compact
 * flavor/size selectors (if this block is ever added to that template),
 * without needing product-specific logic.
 */
const FOOD_INFO_CARET_SVG =
  '<svg class="icon icon-caret" viewBox="0 0 10 6"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

class ProductFoodInfo extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.productFormId;
    this.map = this.readMap();
    this.labels = this.readLabels();
    this.contentEl = this.querySelector('[data-food-info-content]');

    this.changeHandler = (event) => {
      if (!this.isVariantIdInput(event.target)) return;
      this.render(event.target.value);
    };
    document.addEventListener('change', this.changeHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.changeHandler);
  }

  isVariantIdInput(el) {
    if (!el || el.name !== 'id') return false;
    const form = document.getElementById(this.formId);
    return !!(form && form.contains(el));
  }

  readMap() {
    const script = this.querySelector('[data-food-info-map]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return {};
    }
  }

  readLabels() {
    const script = this.querySelector('[data-food-info-labels]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return {};
    }
  }

  render(variantId) {
    const data = this.map[variantId];
    if (!this.contentEl) return;

    // Capture the accordion's current open/closed state before replacing the
    // markup, so a variant switch between two data-having variants preserves
    // it. If no accordion is currently present (hidden, or a variant with no
    // ingredients/allergens/nutrition), this is false - which is also the
    // correct "reset to closed" behaviour once an accordion reappears later.
    const currentDetails = this.contentEl.querySelector('.product-food-info__accordion details');
    const wasOpen = !!(currentDetails && currentDetails.open);

    if (!data || !this.hasAnyData(data)) {
      this.contentEl.innerHTML = '';
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.contentEl.innerHTML = this.buildHtml(data, wasOpen);
  }

  hasAnyData(data) {
    return !!(
      data.flavour_description ||
      data.ingredients ||
      (data.allergens && data.allergens.length) ||
      data.energy ||
      data.fat ||
      data.saturates ||
      data.carbohydrate ||
      data.sugars ||
      data.protein ||
      data.salt ||
      data.net_weight
    );
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  buildHtml(data, openAccordion) {
    let html = '';

    const l = this.labels;

    if (data.flavour_description) {
      html += '<div class="product-food-info__block">';
      html += `<p class="product-food-info__heading">${this.escapeHtml(l.flavour_description_heading)}</p>`;
      html += `<p class="product-food-info__flavour-description">${this.escapeHtml(data.flavour_description)}</p>`;
      html += '</div>';
    }

    if (data.net_weight) {
      html += '<div class="product-food-info__block">';
      html += `<p class="product-food-info__heading">${this.escapeHtml(l.net_weight_heading)}</p>`;
      html += `<p>${this.escapeHtml(data.net_weight)}</p>`;
      html += '</div>';
    }

    const hasAllergens = !!(data.allergens && data.allergens.length);
    const hasNutrition = !!(
      data.energy || data.fat || data.saturates || data.carbohydrate || data.sugars || data.protein || data.salt
    );
    const hasAccordionContent = !!(data.ingredients || hasAllergens || hasNutrition);

    if (hasAccordionContent) {
      html += '<div class="product__accordion accordion product-food-info__accordion">';
      html += `<details${openAccordion ? ' open' : ''}>`;
      html += '<summary><div class="summary__title">';
      html += `<h2 class="h4 accordion__title">${this.escapeHtml(l.accordion_heading)}</h2>`;
      html += '</div>';
      html += FOOD_INFO_CARET_SVG;
      html += '</summary>';
      html += '<div class="accordion__content product-food-info__accordion-content">';

      if (data.ingredients) {
        html += '<div class="product-food-info__block">';
        html += `<p class="product-food-info__heading">${this.escapeHtml(l.ingredients_heading)}</p>`;
        html += `<p class="product-food-info__ingredients">${this.escapeHtml(data.ingredients)}</p>`;
        html += '</div>';
      }

      if (hasAllergens) {
        html += '<div class="product-food-info__block">';
        html += `<p class="product-food-info__heading">${this.escapeHtml(l.allergens_heading)}</p>`;
        html += '<p class="product-food-info__allergens">';
        html += data.allergens
          .map((a) => `<strong class="product-food-info__allergen">${this.escapeHtml(a)}</strong>`)
          .join(', ');
        html += '</p></div>';
      }

      if (hasNutrition) {
        html += '<div class="product-food-info__block">';
        html += `<p class="product-food-info__heading">${this.escapeHtml(l.nutrition_heading)}</p>`;
        html += '<dl class="product-food-info__nutrition">';
        if (data.energy) html += `<div class="product-food-info__nutrition-row"><dt>${this.escapeHtml(l.energy_label)}</dt><dd>${this.escapeHtml(data.energy)}</dd></div>`;
        if (data.fat) html += `<div class="product-food-info__nutrition-row"><dt>${this.escapeHtml(l.fat_label)}</dt><dd>${this.escapeHtml(data.fat)} g</dd></div>`;
        if (data.saturates) html += `<div class="product-food-info__nutrition-row product-food-info__nutrition-row--sub"><dt>${this.escapeHtml(l.saturates_label)}</dt><dd>${this.escapeHtml(data.saturates)} g</dd></div>`;
        if (data.carbohydrate) html += `<div class="product-food-info__nutrition-row"><dt>${this.escapeHtml(l.carbohydrate_label)}</dt><dd>${this.escapeHtml(data.carbohydrate)} g</dd></div>`;
        if (data.sugars) html += `<div class="product-food-info__nutrition-row product-food-info__nutrition-row--sub"><dt>${this.escapeHtml(l.sugars_label)}</dt><dd>${this.escapeHtml(data.sugars)} g</dd></div>`;
        if (data.protein) html += `<div class="product-food-info__nutrition-row"><dt>${this.escapeHtml(l.protein_label)}</dt><dd>${this.escapeHtml(data.protein)} g</dd></div>`;
        if (data.salt) html += `<div class="product-food-info__nutrition-row"><dt>${this.escapeHtml(l.salt_label)}</dt><dd>${this.escapeHtml(data.salt)}</dd></div>`;
        html += '</dl></div>';
      }

      html += '</div></details></div>';
    }

    return html;
  }
}

customElements.define('product-food-info', ProductFoodInfo);
